import {
  FormattedPrice,
  FormattedTime,
  MenuItemWithIcon,
  ModalOpenButton,
} from "@msys/ui";
import { FindReplace as FindReplaceIcon } from "@mui/icons-material";
import WizardIcon from "@mui/icons-material/AutoFixHigh";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import { Button } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import type { To } from "react-router-dom";
import {
  Agreement,
  AskWhom,
  ContractType,
  PermissionName,
} from "../../../clients/graphqlTypes.js";
import { RestrictedByDocumentPermissionWithDebug } from "../../auth/RestrictedByDocumentPermission.js";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { useUserData } from "../../auth/useUserData.js";
import { ConfirmModalProps } from "../../commons/modals/ConfirmProcess.js";
import { QUOTE_ITEM_TYPES } from "../../constants.js";
import { useProjectQuoteContext } from "../../main-routes/projects/ProjectQuoteContext.js";
import { isItemVisibleToOtherSide } from "../../trees-virtual/helpers.js";
import {
  AdditionalItemInput,
  TreeViewMode,
  VirtualItem,
} from "../../trees-virtual/types.js";
import { BareTreeItem } from "../../trees/BareTreeItem.js";
import { BareTreeTableItem } from "../../trees/BareTreeTableItem.js";
import { TreeDataContainer } from "../../trees/components/TreeDataContainer.js";
import { TreeRightIcons } from "../../trees/components/TreeRightIcons.js";
import { InputComponentProps, ItemComponentProps } from "../../trees/types.js";
import { assertNever } from "../../utils.js";
import { itemHasEstimatedCalculationBox } from "../doc-items/app-conditions/boxes.js";
import { TemplatePlaceholderSelectTemplateButton } from "../doc-items/buttons/TemplatePlaceholderSelectTemplateButton.js";
import { CreateItemInput } from "../doc-items/CreateItemInput.js";
import { DecisionContingencyCheckbox } from "../doc-items/fields/DecisionContingencyCheckbox.js";
import { DecisionOptionRadioOrCheckbox } from "../doc-items/fields/DecisionOptionRadioOrCheckbox.js";
import { DocItemContextMenuItems } from "../doc-items/menus/DocItemContextMenuItems.js";
import {
  EstimatedCalculationEmptyRow,
  EstimatedCalculationRow,
} from "../doc-items/tables/EstimatedCalculationRow.js";
import { RefinementAskWhenContext } from "../quotes/isRefinementAskWhenContext.js";
import { QuoteItemCompletionWizardModal } from "../quotes/modals/QuoteItemCompletionWizardModal.js";
import { QuoteRefinementWizardModal } from "../quotes/modals/QuoteRefinementWizardModal.js";
import {
  QuoteTreeItem_Data123Fragment,
  QuoteTreeItem_TreeClientItemFragment,
  QuoteTreeItem_TreeItemFragment,
  QuoteTreeItem_TreeTableItemFragment,
} from "./quote-trees.generated.js";

export function createTreeItemInput<
  Item extends VirtualItem,
  ChildItem extends VirtualItem,
>({
  viewMode,
  quoteId,
  createItem,
  createItemByType,
  createItemFromTemplate,
  createItemsFromTemplates,
  createItemsWithProducts,
  pasteItem,
  isAllowedToPasteItem,
  fetchItemRecommendations,
  createItemFromRecommendation,
}: {
  viewMode: TreeViewMode;
  quoteId: string;
} & Pick<
  React.ComponentProps<typeof CreateItemInput>,
  | "createItem"
  | "createItemByType"
  | "createItemFromTemplate"
  | "createItemsFromTemplates"
  | "createItemsWithProducts"
  | "pasteItem"
  | "isAllowedToPasteItem"
  | "fetchItemRecommendations"
  | "createItemFromRecommendation"
>) {
  return (itemProps: InputComponentProps<Item, ChildItem>) => (
    <CreateItemInput
      viewMode={viewMode}
      doc={{ id: quoteId, docType: "QUOTE", templateIsDraft: false }}
      documentItemTypes={QUOTE_ITEM_TYPES}
      createItem={createItem}
      createItemByType={createItemByType}
      createItemFromTemplate={createItemFromTemplate}
      createItemsFromTemplates={createItemsFromTemplates}
      createItemsWithProducts={createItemsWithProducts}
      pasteItem={pasteItem}
      isAllowedToPasteItem={isAllowedToPasteItem}
      fetchItemRecommendations={fetchItemRecommendations}
      createItemFromRecommendation={createItemFromRecommendation}
      {...itemProps}
    />
  );
}

export const createTreeItem =
  <T extends QuoteTreeItem_TreeItemFragment>({
    pathToDocPage,
    projectId,
    doc,
    docAgreement,
    startConfirmProcess,
    expandedItemIds,
    setItemExpanded,
    pasteItem,
    isAllowedToPasteItem,
    onAdditionalItemInputChange,
    getTo,
    isItemMuted,
  }: {
    pathToDocPage: string;
    projectId: string;
    doc: {
      id: string;
      rootItemId: string;
      viewerPermissions: PermissionName[];
      owningSystemOrganisationId: string;
    };
    docAgreement: Agreement;
    startConfirmProcess: (
      props: ConfirmModalProps
    ) => Promise<boolean | undefined>;
    expandedItemIds: string[] | undefined;
    setItemExpanded: (id: string, expanded: boolean) => void;
    pasteItem: ((docId: string, parentItemId: string) => Promise<void>) | null;
    isAllowedToPasteItem: (parentItem: T) => boolean;
    onAdditionalItemInputChange: ((input: AdditionalItemInput) => void) | null;
    getTo: (itemProps: ItemComponentProps<T>) => To;
    isItemMuted: (itemProps: ItemComponentProps<T>) => boolean;
  }) =>
  (itemProps: ItemComponentProps<T>) => {
    const { refetchWizardQuestions } = useProjectQuoteContext();

    const [Data123Left, Data123Right] = React.useMemo(
      () => getData123(itemProps.item, doc),
      [itemProps.item]
    );

    const IconRightButtons = React.useMemo(
      () => (
        <>
          {itemProps.item.placeholderForTemplateType ? (
            <TemplatePlaceholderSelectTemplateButton
              projectId={projectId}
              docId={doc.id}
              itemId={itemProps.item.id}
              templateTypeName={
                itemProps.item.placeholderForTemplateType.templateType.title
              }
              templateSearchFilterDefaults={
                itemProps.item.templateSearchFilterDefaults
              }
              templateSearchSortingDefaults={
                itemProps.item.templateSearchSortingDefaults
              }
              templateApplicableFor="QUOTE"
              onCompleted={id => setItemExpanded(id, true)}
              type="icon"
              color="primary"
              variant="outlined"
              size="medium"
              Icon={FindReplaceIcon}
              refetchWizardQuestions={refetchWizardQuestions}
            />
          ) : null}
          <DecisionWizardButtons
            projectId={projectId}
            quote={doc}
            item={itemProps.item}
          />
          {itemProps.item.decisionIsContingentItem &&
            !itemProps.item.isItemEliminated && (
              <DecisionContingencyCheckbox
                projectId={projectId}
                docId={doc.id}
                itemId={itemProps.item.id}
                item={itemProps.item}
                disabled={itemProps.item.deletedAt !== null}
              />
            )}
          {itemProps.parentItem?.type === "decision" &&
            !itemProps.item.decisionOptionElimination &&
            !itemProps.item.isItemEliminated && (
              <DecisionOptionRadioOrCheckbox
                projectId={projectId}
                docId={doc.id}
                item={itemProps.item}
                disabled={itemProps.item.deletedAt !== null}
                expandedItemIds={expandedItemIds}
              />
            )}
        </>
      ),
      [itemProps.item, itemProps.parentItem?.type, refetchWizardQuestions]
    );

    const ContextMenu = React.useMemo(
      () => (
        <DocItemContextMenuItems
          pathToDocPage={pathToDocPage}
          docId={doc.id}
          docType="QUOTE"
          projectId={projectId}
          item={itemProps.item}
          itemParentType={itemProps.parentItem?.type ?? null}
          startConfirmProcess={startConfirmProcess}
          expandedItemIds={expandedItemIds}
          pasteItem={isAllowedToPasteItem(itemProps.item) ? pasteItem : null}
          onAdditionalItemInputChange={onAdditionalItemInputChange}
        />
      ),
      [itemProps.item, itemProps.parentItem?.type]
    );

    const RightIcons = React.useMemo(
      () => (
        <TreeRightIcons
          document={doc}
          item={itemProps.item}
          isVisibleToOtherSide={isItemVisibleToOtherSide(
            itemProps.item,
            itemProps.allItems
          )}
        />
      ),
      [itemProps.item, itemProps.allItems]
    );

    return (
      <BareTreeItem
        docAgreement={docAgreement}
        Data123Left={Data123Left}
        Data123Right={Data123Right}
        IconRightButtons={IconRightButtons}
        ContextMenu={ContextMenu}
        RightIcons={RightIcons}
        showPath={itemProps.parentItem?.type !== "decision"}
        item={itemProps.item}
        title={
          itemProps.item.pendingChangeAttributes?.title ?? itemProps.item.title
        }
        pathForPdf={
          itemProps.item.pendingChangeAttributes?.pathForPdf ??
          itemProps.item.pathForPdf
        }
        isRootItem={itemProps.isRootItem}
        depth={itemProps.depth}
        isGreyedOut={itemProps.isGreyedOut}
        isHidden={itemProps.isHidden}
        isPriceHidden={itemProps.isPriceHidden}
        subcontractPath={itemProps.subcontractPath}
        collapseButton={itemProps.collapseButton}
        onClick={() => {
          setItemExpanded(itemProps.item.id, true);
        }}
        to={getTo(itemProps)}
        isMuted={itemProps.isMuted ?? isItemMuted(itemProps)}
      />
    );
  };

export const createTreeClientItem =
  <T extends QuoteTreeItem_TreeClientItemFragment>({
    projectId,
    doc,
    docAgreement,
    expandedItemIds,
  }: {
    projectId: string;
    doc: {
      id: string;
      rootItemId: string;
      owningSystemOrganisationId: string;
      viewerPermissions: PermissionName[];
    };
    docAgreement: Agreement;
    expandedItemIds: string[] | undefined;
  }) =>
  (itemProps: ItemComponentProps<T>) => {
    const { activeWizardContext, hasItemAnyQuestions, refetchWizardQuestions } =
      useProjectQuoteContext();

    const [Data123Left, Data123Right] = React.useMemo(
      () => getData123(itemProps.item, doc),
      [itemProps.item]
    );

    const IconRightButtons = React.useMemo(
      () => (
        <>
          {activeWizardContext === "onQuoteRefinement" &&
            hasItemAnyQuestions(itemProps.item.id) && (
              <RefinementWizardButton
                projectId={projectId}
                quote={doc}
                item={itemProps.item}
                askWhen="onQuoteRefinement"
                refetchAfterApplyItemAction={refetchWizardQuestions}
              />
            )}
          {itemProps.item.decisionIsContingentItem &&
            !itemProps.item.isItemEliminated && (
              <DecisionContingencyCheckbox
                projectId={projectId}
                docId={doc.id}
                itemId={itemProps.item.id}
                item={itemProps.item}
              />
            )}
          {itemProps.parentItem?.type === "decision" &&
            !itemProps.item.decisionOptionElimination &&
            !itemProps.item.isItemEliminated && (
              <DecisionOptionRadioOrCheckbox
                projectId={projectId}
                docId={doc.id}
                item={itemProps.item}
                expandedItemIds={expandedItemIds}
              />
            )}
        </>
      ),
      [
        activeWizardContext,
        hasItemAnyQuestions,
        itemProps.item,
        itemProps.parentItem?.type,
        refetchWizardQuestions,
      ]
    );

    const RightIcons = React.useMemo(
      () => (
        <TreeRightIcons
          document={doc}
          item={itemProps.item}
          isVisibleToOtherSide={isItemVisibleToOtherSide(
            itemProps.item,
            itemProps.allItems
          )}
        />
      ),
      [itemProps.item, itemProps.allItems]
    );

    return (
      <BareTreeItem
        docAgreement={docAgreement}
        Data123Left={Data123Left}
        Data123Right={Data123Right}
        IconRightButtons={IconRightButtons}
        RightIcons={RightIcons}
        showPath={itemProps.parentItem?.type !== "decision"}
        item={itemProps.item}
        title={
          itemProps.item.pendingChangeAttributes?.title ?? itemProps.item.title
        }
        pathForPdf={
          itemProps.item.pendingChangeAttributes?.pathForPdf ??
          itemProps.item.pathForPdf
        }
        isRootItem={itemProps.isRootItem}
        depth={itemProps.depth}
        isGreyedOut={itemProps.isGreyedOut}
        isHidden={itemProps.isHidden}
        isPriceHidden={itemProps.isPriceHidden}
        subcontractPath={itemProps.subcontractPath}
        collapseButton={itemProps.collapseButton}
      />
    );
  };

export const createTreeTableItem =
  <T extends QuoteTreeItem_TreeTableItemFragment>({
    pathToDocPage,
    projectId,
    doc,
    docAgreement,
    startConfirmProcess,
    navigateToItem,
    expandedItemIds,
    setItemExpanded,
    pasteItem,
    viewerDecisionRole,
    onAdditionalItemInputChange,
    isItemMuted,
  }: {
    pathToDocPage: string;
    projectId: string;
    doc: {
      id: string;
      rootItemId: string;
      contractType: ContractType;
      viewerPermissions: PermissionName[];
      owningSystemOrganisationId: string;
    };
    docAgreement: Agreement;
    startConfirmProcess: (
      props: ConfirmModalProps
    ) => Promise<boolean | undefined>;
    navigateToItem: (id: string) => void;
    expandedItemIds: string[] | undefined;
    setItemExpanded: (id: string, expanded: boolean) => void;
    pasteItem: ((docId: string, parentItemId: string) => Promise<void>) | null;
    isAllowedToPasteItem: (parentItem: T) => boolean;
    viewerDecisionRole: AskWhom | undefined;
    onAdditionalItemInputChange: ((input: AdditionalItemInput) => void) | null;
    isItemMuted: (itemProps: ItemComponentProps<T>) => boolean;
  }) =>
  (itemProps: ItemComponentProps<T>) => {
    const [Data123Left, Data123Right] = React.useMemo(
      () => getData123(itemProps.item, doc),
      [itemProps.item]
    );

    const IconRightButtons = React.useMemo(
      () => (
        <>
          <DecisionWizardButtons
            projectId={projectId}
            quote={doc}
            item={itemProps.item}
          />
          {itemProps.item.decisionIsContingentItem &&
            !itemProps.item.isItemEliminated && (
              <DecisionContingencyCheckbox
                projectId={projectId}
                docId={doc.id}
                itemId={itemProps.item.id}
                item={itemProps.item}
              />
            )}
          {itemProps.parentItem?.type === "decision" &&
            !itemProps.item.decisionOptionElimination &&
            !itemProps.item.isItemEliminated && (
              <DecisionOptionRadioOrCheckbox
                projectId={projectId}
                docId={doc.id}
                item={itemProps.item}
                expandedItemIds={expandedItemIds}
              />
            )}
        </>
      ),
      [itemProps.item, itemProps.parentItem]
    );

    const ContextMenu = React.useMemo(
      () => (
        <DocItemContextMenuItems
          pathToDocPage={pathToDocPage}
          docId={doc.id}
          docType="QUOTE"
          projectId={projectId}
          item={itemProps.item}
          itemParentType={itemProps.parentItem?.type ?? null}
          startConfirmProcess={startConfirmProcess}
          expandedItemIds={expandedItemIds}
          pasteItem={pasteItem}
          onAdditionalItemInputChange={onAdditionalItemInputChange}
        />
      ),
      [itemProps.item, itemProps.parentItem?.type]
    );

    const RightIcons = React.useMemo(
      () => (
        <TreeRightIcons
          document={doc}
          item={itemProps.item}
          isVisibleToOtherSide={isItemVisibleToOtherSide(
            itemProps.item,
            itemProps.allItems
          )}
        />
      ),
      [itemProps.item, itemProps.allItems]
    );

    const FormRow = React.useMemo(() => {
      const hasEstimatedCalculationBox = itemHasEstimatedCalculationBox(
        itemProps.item
      );
      return hasEstimatedCalculationBox ? (
        <EstimatedCalculationRow
          itemId={itemProps.item.id}
          projectId={projectId}
          docId={doc.id}
          item={itemProps.item}
          contractType={doc.contractType}
        />
      ) : (
        <EstimatedCalculationEmptyRow />
      );
    }, [itemProps.item]);

    return (
      <BareTreeTableItem
        docAgreement={docAgreement}
        Data123Left={Data123Left}
        Data123Right={Data123Right}
        FormRow={FormRow}
        IconRightButtons={IconRightButtons}
        ContextMenu={ContextMenu}
        RightIcons={RightIcons}
        showPath={itemProps.parentItem?.type !== "decision"}
        item={itemProps.item}
        title={
          itemProps.item.pendingChangeAttributes?.title ?? itemProps.item.title
        }
        pathForPdf={
          itemProps.item.pendingChangeAttributes?.pathForPdf ??
          itemProps.item.pathForPdf
        }
        isRootItem={itemProps.isRootItem}
        depth={itemProps.depth}
        isGreyedOut={itemProps.isGreyedOut}
        isHidden={itemProps.isHidden}
        isPriceHidden={itemProps.isPriceHidden}
        subcontractPath={itemProps.subcontractPath}
        collapseButton={itemProps.collapseButton}
        onClick={() => {
          navigateToItem(itemProps.item.id);
          setItemExpanded(itemProps.item.id, true);
        }}
        isMuted={itemProps.isMuted ?? isItemMuted(itemProps)}
      />
    );
  };

export const getData123 = (
  item: QuoteTreeItem_Data123Fragment,
  doc: {
    viewerPermissions: PermissionName[];
  }
): [JSX.Element | null, JSX.Element | null] => {
  switch (item.type) {
    case "paid": {
      // TODO this is causing issues for deleted items; for now just show "null" as value instead of breaking
      const calculateForQuote = item.proposedCalculation;
      if (!calculateForQuote) return [null, null];

      return [
        <TreeDataContainer key={"left"}>
          <div>{calculateForQuote.quantity}x</div>
          {calculateForQuote.timePerUnit > 0 && (
            <RestrictedByDocumentPermissionWithDebug
              permission="quote_document_read_calculation"
              document={doc}
            >
              {
                <div>
                  <FormattedTime value={calculateForQuote.timePerUnit} />
                </div>
              }
            </RestrictedByDocumentPermissionWithDebug>
          )}
          {calculateForQuote.quantity !== 1 && (
            <RestrictedByDocumentPermissionWithDebug
              permission="quote_document_read"
              document={doc}
            >
              <div>
                <FormattedPrice value={calculateForQuote.pricePerUnit} />
              </div>
            </RestrictedByDocumentPermissionWithDebug>
          )}
          <RestrictedByDocumentPermissionWithDebug
            permission="quote_document_read"
            document={doc}
          >
            <div style={{ fontWeight: "bold" }}>
              <FormattedPrice value={calculateForQuote.priceSubTotal} />
            </div>
          </RestrictedByDocumentPermissionWithDebug>
        </TreeDataContainer>,
        null,
      ];
    }
    case "section": {
      const calculateForQuote = item.proposedCalculation;

      if (!calculateForQuote) return [null, null];

      if (
        calculateForQuote.timePerUnit === 0 &&
        calculateForQuote.pricePerUnit === 0 &&
        calculateForQuote.quantity === 1
      ) {
        return [null, null];
      }
      return [
        null,
        <TreeDataContainer key={"right"}>
          {calculateForQuote.quantity !== 1 && (
            <>
              <div>{calculateForQuote.quantity}x</div>
              {calculateForQuote.timePerUnit !== 0 && (
                <RestrictedByDocumentPermissionWithDebug
                  permission="quote_document_read_calculation"
                  document={doc}
                >
                  {
                    <div>
                      <FormattedTime value={calculateForQuote.timePerUnit} />
                    </div>
                  }
                </RestrictedByDocumentPermissionWithDebug>
              )}
              {calculateForQuote.pricePerUnit !== 0 && (
                <RestrictedByDocumentPermissionWithDebug
                  permission="quote_document_read"
                  document={doc}
                >
                  <div>
                    <FormattedPrice value={calculateForQuote.pricePerUnit} />
                  </div>
                </RestrictedByDocumentPermissionWithDebug>
              )}
            </>
          )}
          <RestrictedByDocumentPermissionWithDebug
            permission="quote_document_read"
            document={doc}
          >
            {calculateForQuote.timeTotal !== 0 && (
              <div style={{ fontWeight: "bold" }}>
                <FormattedTime value={calculateForQuote.timeTotal} />
              </div>
            )}
            {calculateForQuote.priceSubTotal !== 0 && (
              <div style={{ fontWeight: "bold" }}>
                <FormattedPrice value={calculateForQuote.priceSubTotal} />
              </div>
            )}
          </RestrictedByDocumentPermissionWithDebug>
        </TreeDataContainer>,
      ];
    }
    case "decision": {
      const calculateForQuote = item.proposedCalculation;

      if (!calculateForQuote) return [null, null];

      if (
        calculateForQuote.timePerUnit === 0 &&
        calculateForQuote.pricePerUnit === 0 &&
        calculateForQuote.quantity === 1
      ) {
        return [null, null];
      }
      return [
        null,
        <TreeDataContainer key={"right"}>
          {calculateForQuote.quantity !== 1 && (
            <>
              <div>{calculateForQuote.quantity}x</div>
              {calculateForQuote.timePerUnit !== 0 && (
                <RestrictedByDocumentPermissionWithDebug
                  permission="quote_document_read_calculation"
                  document={doc}
                >
                  {
                    <div>
                      <FormattedTime value={calculateForQuote.timePerUnit} />
                    </div>
                  }
                </RestrictedByDocumentPermissionWithDebug>
              )}
              {calculateForQuote.pricePerUnit !== 0 && (
                <RestrictedByDocumentPermissionWithDebug
                  permission="quote_document_read"
                  document={doc}
                >
                  <div>
                    <FormattedPrice value={calculateForQuote.pricePerUnit} />
                  </div>
                </RestrictedByDocumentPermissionWithDebug>
              )}
            </>
          )}
          <RestrictedByDocumentPermissionWithDebug
            permission="quote_document_read"
            document={doc}
          >
            {calculateForQuote.timeTotal !== 0 && (
              <div style={{ fontWeight: "bold" }}>
                <FormattedTime
                  value={calculateForQuote.timeTotal}
                  notSetText=""
                />
              </div>
            )}
            {calculateForQuote.priceSubTotal !== 0 && (
              <div style={{ fontWeight: "bold" }}>
                <FormattedPrice value={calculateForQuote.priceSubTotal} />
              </div>
            )}
          </RestrictedByDocumentPermissionWithDebug>
        </TreeDataContainer>,
      ];
    }
    case "unpaid":
    case "defect": {
      return [null, null];
    }
    default:
      throw assertNever(item.type);
  }
};

export const DecisionWizardButtons = ({
  projectId,
  quote,
  item,
  disabled = false,
  type = "icon-button",
}: {
  projectId: string;
  quote: {
    id: string;
    owningSystemOrganisationId: string;
    viewerPermissions: PermissionName[];
  };
  item: { id: string };
  disabled?: boolean;
  type?: "icon-button" | "menu-item";
}) => {
  const { activeWizardContext, hasItemAnyQuestions, refetchWizardQuestions } =
    useProjectQuoteContext();

  if (!hasItemAnyQuestions(item.id)) return null;

  switch (activeWizardContext) {
    case "item":
      return (
        <ItemWizardButton
          projectId={projectId}
          quote={quote}
          item={item}
          refetchAfterApplyItemAction={refetchWizardQuestions}
          disabled={disabled}
          type={type}
        />
      );
    case "onQuoteRefinement":
    case "onQuoteRefinement2":
    case "onQuoteRefinement3":
      return (
        <RefinementWizardButton
          projectId={projectId}
          quote={quote}
          item={item}
          askWhen={activeWizardContext}
          refetchAfterApplyItemAction={refetchWizardQuestions}
          disabled={disabled}
          type={type}
        />
      );
    default:
      assertNever(activeWizardContext);
  }
};

const ItemWizardButton = ({
  projectId,
  quote,
  item,
  refetchAfterApplyItemAction,
  disabled,
  type = "icon-button",
}: {
  projectId: string;
  quote: { id: string; viewerPermissions: PermissionName[] };
  item: { id: string };
  refetchAfterApplyItemAction: () => Promise<any>;
  disabled?: boolean;
  type?: "icon-button" | "menu-item";
}) => {
  const { t } = useTranslate(["QuoteItem"]);

  return (
    <RestrictedByDocumentPermissionWithDebug
      permission="quote_document_update"
      document={quote}
      key={"decision-wizard"}
    >
      <div
        onMouseDown={e => {
          e.stopPropagation();
        }}
        onTouchStart={e => {
          e.stopPropagation();
        }}
        onClick={e => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <ModalOpenButton
          Modal={QuoteItemCompletionWizardModal}
          modalProps={{
            projectId: projectId,
            quoteId: quote.id,
            itemId: item.id,
            refetchAfterApplyItemAction,
          }}
        >
          {type === "menu-item" ? (
            <MenuItemWithIcon
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
              }}
              icon={<AutoFixHighIcon />}
            >
              {t("Open item wizard", { ns: "QuoteItem" })}
            </MenuItemWithIcon>
          ) : (
            <Button
              color={"primary"}
              variant="outlined"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
              }}
              sx={{ minWidth: "auto", padding: 5 / 8 }}
              disabled={disabled}
            >
              <QuestionMarkIcon
                fontSize="small"
                style={{ fontSize: "1.15rem" }}
              />
            </Button>
          )}
        </ModalOpenButton>
      </div>
    </RestrictedByDocumentPermissionWithDebug>
  );
};

export const RefinementWizardButton = ({
  projectId,
  quote,
  item,
  askWhen = "onQuoteRefinement",
  refetchAfterApplyItemAction,
  disabled,
  type = "icon-button",
}: {
  projectId: string;
  quote: {
    id: string;
    owningSystemOrganisationId: string;
    viewerPermissions: PermissionName[];
  };
  item: { id: string };
  askWhen: RefinementAskWhenContext;
  refetchAfterApplyItemAction: () => Promise<any>;
  disabled?: boolean;
  type?: "icon-button" | "menu-item";
}) => {
  const { currentUser } = useUserData();
  const { t } = useTranslate(["QuoteItem"]);

  return (
    <RestrictedByDocumentPermissionWithDebug
      permission="quote_decisions_decide"
      document={quote}
    >
      <div
        onMouseDown={e => {
          e.stopPropagation();
        }}
        onTouchStart={e => {
          e.stopPropagation();
        }}
        onClick={e => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <ModalOpenButton
          Modal={QuoteRefinementWizardModal}
          modalProps={{
            projectId: projectId,
            quoteId: quote.id,
            startingNode: {
              type: "itemId",
              itemId: item.id,
            },
            refetchAfterApplyItemAction,
            askWhen,
            askWhom:
              currentUser!.organisation.id === quote.owningSystemOrganisationId
                ? "contractor"
                : "contractee",
          }}
        >
          {type === "menu-item" ? (
            <MenuItemWithIcon
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
              }}
              icon={<AutoFixHighIcon />}
            >
              {t("Open refinement wizard", { ns: "QuoteItem" })}
            </MenuItemWithIcon>
          ) : (
            <Button
              color={"primary"}
              variant="outlined"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
              }}
              sx={{ minWidth: "auto", padding: 5 / 8 }}
              disabled={disabled}
            >
              <WizardIcon fontSize="small" style={{ fontSize: "1.15rem" }} />
            </Button>
          )}
        </ModalOpenButton>
      </div>
    </RestrictedByDocumentPermissionWithDebug>
  );
};
